'use strict';


import { Buildings, settingsData, threeJsVariables, UIRoot } from '../../settings';
import { BaseElement } from '../interfaces/BaseElement';
import { Vantages } from '../components/vantage/Vantage';
import {
	ClampToEdgeWrapping,
	Group,
	LinearSRGBColorSpace,
	LoadingManager, MathUtils,
	Matrix4,
	PerspectiveCamera,
	Scene,
	TextureLoader, Vector3,
	WebGLRenderer,
} from 'three';
import {Draggable} from "gsap/Draggable";
import { OrbitControls } from '../../libs/OrbitControls';
import { disposeObject, isBetaResourcesFolder } from '../../functions';
import { generateRegion } from '../threejs/sceneBackground';
import { Cloudflare } from '../api/Cloudflare';
import { DreamsPreloader } from '../components/preloader/DreamsPreloader';
import gsap from "gsap";
import { zoom } from '../../libs/directive';
import { Compass } from '../components/compass/compass';

export class QAFloorValidator {
	constructor (renderer) {
		this.renderer = renderer;
		this.floor = settingsData.currentFloor;
		const ls = localStorage.getItem('floorValidator');
		this.ls = ls ? JSON.parse(ls) : {};
		this.cards = [];
		this.container = new BaseElement()
			.addClass('qa__floor-validator')
			.DOMElement;
		
		const header = new BaseElement()
			.addClass('qa__floor-validator__drag')
			.innerHtml(`Floor validator: ${this.floor}`)
			.appendTo(this.container)
			.DOMElement;
		
		gsap.registerPlugin(Draggable);
		Draggable.create(this.container, {
				type: "x,y",
				trigger: header
			}
		);
	}
	
	build() {
		const building = Buildings.getActiveBuilding();
		const apartments = building.getFloorSelections(this.floor,false);
		const props = apartments.map(apt => apt.userData.crm_data);
		let prevValid = [];
		
		if (this.ls[building.name] && this.ls[building.name][this.floor]) {
			prevValid = this.ls[building.name][this.floor];
		}
		
		props.forEach(prop => {
			const status = prevValid.find(data => data.apt === prop.propNum) || {valid: false,message: 'Not checked'};
			const card = new AptCardValidator(prop,status,this.renderer);
			card.build(this.container);
			this.cards.push(card);
		});
		
		UIRoot.appendChild(this.container);
	}
	
	save() {
		const activeBuilding = Buildings.getActiveBuilding();
		if (!this.ls[activeBuilding.name]) {
			this.ls[activeBuilding.name] = {};
		}
		
		this.ls[activeBuilding.name][this.floor] = this.cards.map(card => card.status);
		localStorage.setItem('floorValidator',JSON.stringify(this.ls));
	}
	
	destroy() {
		for (let i = 0; i < this.cards.length; i++) {
			this.cards[i].destroy();
		}
		
		this.cards = null;
		this.ls = null;
		this.floor = null;
		this.container.remove();
		this.container = null;
	}
	
}


class AptCardValidator {
	constructor (props,status,renderer) {
		this.data = props;
		this.renderer = renderer;
		this.vantage = null;
		this.status = {
			apt: this.data.propNum,
			message: status.message,
			valid: status.valid
		};
		this.card = new BaseElement()
			.addClass('qa__floor-validator__card')
			.DOMElement;
	}
	
	build(parent) {
		const header = new BaseElement('h5')
			.addClass('qa__floor-validator__card__header')
			.innerHtml('APT: ' + this.data.propNum)
			.appendTo(this.card)
			.DOMElement;
		
		const status = new BaseElement()
			.addClass('qa__floor-validator__card__valid', this.status.valid ? '--floor-validator__valid' : '--floor-validator__invalid')
			.appendTo(header);
		
		const content = new BaseElement()
			.addClass('qa__floor-validator__card__content')
			.appendTo(this.card)
			.DOMElement;
		
		const plan2D = new BaseElement()
			.addClass('qa__floor-validator__card__element')
			.appendTo(content)
			.DOMElement;
		
		const vantageViewer = new BaseElement()
			.addClass('qa__floor-validator__card__element','qa__floor-validator__vantage-viewer')
			.appendTo(content)
			.DOMElement;
		
		const buttons = new BaseElement()
			.addClass('qa__floor-validator__card__buttons')
			.appendTo(this.card)
			.DOMElement;
		
		new BaseElement('button')
			.addClass('qa__floor-validator__card__btn','icon-close','button')
			.event('click',() => {
				status.removeClass('--floor-validator__valid');
				status.addClass('--floor-validator__invalid');
				this.status.valid = false;
			})
			.appendTo(buttons);
		
		new BaseElement('button')
			.addClass('qa__floor-validator__card__btn','icon-check-mark','button')
			.event('click',() => {
				status.addClass('--floor-validator__valid');
				status.removeClass('--floor-validator__invalid');
				this.status.valid = true;
			})
			.appendTo(buttons);
		
		const img = new Image();
		img.classList.add('qa__floor-validator__card__plan2D');
		img.alt = `APT: ${this.data.propNum} 2D plan`;
		img.onload = () => {
			plan2D.appendChild(img);
			plan2D.classList.add('zoom');
			plan2D.addEventListener('mouseenter',() => parent.classList.add('--prevent-scroll'));
			plan2D.addEventListener('mouseleave',() => parent.classList.remove('--prevent-scroll'));
			zoom();
		};
		img.onerror = () => {
			QAErrorMessageElement(plan2D,`2D plan is absent`);
			this.status.message = '2D plan is absent';
		};
		img.src = Cloudflare.resize(this.data.assets.plan2D,{width: 4096});
		
		const bundleVantages = threeJsVariables.bundleConfig?.vantage || {};
		
		const loadVantageIframe = () => {
			if (this.data.vantage.length) {
				new BaseElement('iframe')
					.addClass('qa__floor-validator__vantage-iframe')
					.addAttribute('src',this.data.vantage)
					.appendTo(vantageViewer);
			} else {
				QAErrorMessageElement(vantageViewer,'Vantage is absent');
				this.status.message = 'Vantage is absent';
			}
		};
		
		if (Vantages.isPhotographic() && bundleVantages && Object.keys(bundleVantages).length) {
			const activeBuilding = Buildings.getActiveBuilding();
			const vantage = bundleVantages[activeBuilding.name].find(vantage => vantage.name === this.data.engineId);
			if (vantage) {
				this.vantage = new QAVantageViewer(vantage,vantageViewer,this.renderer);
				this.vantage.loadVantage();
			} else {
				loadVantageIframe();
			}
		} else {
			loadVantageIframe();
		}
		
		parent.appendChild(this.card);
	}
	
	destroy() {
		this.vantage?.destroy();
		this.card = null;
		this.data = null;
		this.status = null;
		this.vantage = null;
	}
	
}


class QAVantageViewer {
	constructor (vantage,container,renderer) {
		this.vantage = vantage;
		this.params = {
			matrix: new Matrix4().fromArray(this.vantage.matrix),
			from: new Vector3().fromArray(this.vantage.pointFrom),
			to: new Vector3().fromArray(this.vantage.pointTo),
			parts: ['nz','pz','px','nx','py','ny'],
			url: `${config.cdnRepository}/${config.projectFolder}/`
		};
		
		this.container = container;
		
		this.canvas = new BaseElement('canvas')
			.appendTo(this.container)
			.DOMElement;
		
		this.context = null;
		
		this.scene = new Scene();
		this.camera = new PerspectiveCamera(90);
		this.renderer = renderer;
		
		this.sphere = null;
		
		// this.container.appendChild(this.renderer.domElement);
		this.controls = new OrbitControls(this.camera,this.container);
		this.controls.rotateSpeed = 0.15;
		this.controls.enablePan = false;
		this.controls.enableZoom = false;
		this.controls.reverseControls = true;
		this.controls.minAzimuthAngle = -Infinity;
		this.controls.maxAzimuthAngle = Infinity;
		this.controls.update();
		
		this.params.from.applyMatrix4(this.params.matrix);
		this.params.to.applyMatrix4(this.params.matrix);
		this.camera.position.copy(this.params.from);
		this.controls.target.copy(this.params.to);
		this.controls.update();
		
		const limit = MathUtils.degToRad(this.vantage.limit);
		
		if (limit === Math.PI * 2) {
			this.controls.minAzimuthAngle = - Infinity;
			this.controls.maxAzimuthAngle = Infinity;
		} else {
			const currentAzimuth = this.controls.getAzimuthalAngle();
			this.controls.minAzimuthAngle = currentAzimuth - limit / 2;
			this.controls.maxAzimuthAngle = currentAzimuth + limit / 2;
		}
		
		this.compass = new Compass(this.container,this.vantage.name,this.camera,this.controls);
		this.compass.addClass('vantage-compass');
		this.compass.rotate();
		
		this.controls.addEventListener('change',this.render.bind(this));
	}
	
	loadVantage() {
		const manager = new LoadingManager();
		const textureLoader = new TextureLoader(manager);
		const preloader = new DreamsPreloader(this.container);
		preloader.substrate().absolute();
		
		manager.onStart = () => {
			preloader.show();
		};
		
		manager.onLoad = () => {
			if (this.container) {
				preloader.remove();
				const {width} = this.container.getBoundingClientRect();
				const aspect = 1.541902573131066;
				const height = width / aspect;
				const ratio = Math.min(window.devicePixelRatio, 2);
				this.canvas.width = width * ratio;
				this.canvas.height = height * ratio;
				this.context = this.canvas.getContext('2d');
				this.camera.aspect = width / height;
				this.camera.updateProjectionMatrix();
				this.render();
			}
		};
		
		this.sphere = new Group();
		this.sphere.name = 'Vantage';
		const vantageURL = isBetaResourcesFolder() ?
			this.vantage.url.replace(/vantages3D/g,'vantages3D_beta') :
			this.vantage.url;
		const url = `${this.params.url}/${vantageURL}/`;
		
		this.params.parts.forEach(name => {
			let mesh;
			let modifiedUrl;
			
			switch (name) {
				case 'nz':
					mesh = generateRegion([0,0,-1],[0,1,0],[ 1,0,0]);
					break;
				case 'pz':
					mesh = generateRegion([0,0, 1],[0,1,0],[-1,0,0]);
					break;
				case 'px':
					mesh = generateRegion([ 1,0,0],[0,1,0],[0,0, 1]);
					break;
				case 'nx':
					mesh = generateRegion([-1,0,0],[0,1,0],[0,0,-1]);
					break;
				case 'py':
					mesh = generateRegion([0,0, 1],[1,0,0],[0, 1,0]);
					break;
				case 'ny':
					mesh = generateRegion([0,0,-1],[1,0,0],[0,-1,0]);
					mesh.rotation.y = Math.PI;
					break;
			}
			
			modifiedUrl = url + name + '.jpg';
			modifiedUrl = Cloudflare.resize(modifiedUrl,{width: 1024});
			const texture = textureLoader.load(modifiedUrl);
			texture.wrapS = ClampToEdgeWrapping;
			texture.wrapT = ClampToEdgeWrapping;
			mesh.material.uniforms.map1.value = texture;
			mesh.name = name;
			this.sphere.position.copy(this.params.from);
			this.sphere.add(mesh);
		});
		
		this.scene.add(this.sphere);
	}
	
	render() {
		this.compass.rotate();
		this.renderer.render(this.scene,this.camera);
		this.context.drawImage(this.renderer.domElement,0,0);
	}
	
	destroy() {
		this.sphere.removeFromParent();
		this.sphere.traverse(el => {
			if (el.isMesh) {
				el.geometry.dispose();
				el.material.uniforms.map1.value.dispose();
				el.material.dispose();
			}
		});
		this.controls.dispose();
		this.controls = null;
		this.canvas.remove();
		this.canvas = null;
		this.context = null;
		this.renderer = null;
		this.camera = null;
		this.container = null;
		this.scene = null;
		this.sphere = null;
		this.params = null;
		this.vantage = null;
	}
	
}


function QAErrorMessageElement (parent,message) {
	new BaseElement('span')
		.addClass('qa__floor-validator__message')
		.innerHtml(message)
		.appendTo(parent);
}
